import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useState, useEffect } from 'react';
import {
  LayoutDashboard, Globe, ArrowLeftRight, AlertTriangle,
  FileBarChart2, BarChart3, LogOut, Shield, ChevronRight,
  Bell, Settings, Landmark, Users as UsersIcon, Sun, Moon, Wallet,
  ScrollText, Activity, AlertOctagon, GitCommitHorizontal, Menu, X, GitCompare, Database,
} from 'lucide-react';
import { useAuthStore } from '../store/auth.store';
import { queryClient } from '../lib/queryClient';

const ALL_NAV_ITEMS = [
  { to: '/overview',      label: 'Overview',       icon: LayoutDashboard,  group: 'main',      adminOnly: false },
  { to: '/worlds',        label: 'Worlds',          icon: Globe,            group: 'main',      adminOnly: false },
  { to: '/holders',          label: 'Holders',          icon: Wallet,                 group: 'main',      adminOnly: false },
  { to: '/holder-timeline', label: 'Holder Timeline',  icon: GitCommitHorizontal,    group: 'main',      adminOnly: false },
  { to: '/users',         label: 'Users',           icon: UsersIcon,        group: 'main',      adminOnly: true  },
  { to: '/central-bank',  label: 'Central Bank',   icon: Landmark,         group: 'finance',   adminOnly: true  },
  { to: '/exchange-rates',label: 'Exchange Rates',  icon: ArrowLeftRight,   group: 'finance',   adminOnly: false },
  { to: '/anomalies',     label: 'Anomalies',       icon: AlertTriangle,    group: 'finance',   adminOnly: false },
  { to: '/audit-log',     label: 'Audit Log',       icon: ScrollText,       group: 'finance',   adminOnly: false },
  { to: '/reports',       label: 'Reports',         icon: FileBarChart2,    group: 'finance',   adminOnly: false },
  { to: '/grafana',       label: 'Dashboards',      icon: BarChart3,        group: 'analytics', adminOnly: false },
  { to: '/service-health',label: 'Service Health',  icon: Activity,         group: 'analytics', adminOnly: true  },
  { to: '/dlq',           label: 'DLQ Monitor',     icon: AlertOctagon,     group: 'analytics', adminOnly: true  },
  { to: '/redpanda',      label: 'Redpanda',         icon: Database,         group: 'analytics', adminOnly: true  },
  { to: '/world-compare', label: 'World Compare',   icon: GitCompare,       group: 'main',      adminOnly: false },
];

const groups: { key: string; label: string; partnerLabel?: string }[] = [
  { key: 'main',      label: 'PLATFORM',  partnerLabel: 'MY PORTAL' },
  { key: 'finance',   label: 'FINANCE' },
  { key: 'analytics', label: 'ANALYTICS' },
];

function roleColor(role: string | null) {
  if (!role) return 'text-slate-500';
  if (role.includes('CENTRAL_BANK')) return 'text-cyan-400';
  if (role.includes('ADMIN_READONLY')) return 'text-amber-400';
  if (role.includes('WORLD_OWNER')) return 'text-purple-400';
  return 'text-emerald-400';
}

export function DashboardLayout() {
  const { email, role, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const isPartner = role === 'WORLD_OWNER';
  const navItems = ALL_NAV_ITEMS.filter((n) => !n.adminOnly || !isPartner);

  const [isDark, setIsDark] = useState(() => {
    const stored = localStorage.getItem('ukfms-theme');
    return stored !== 'light';
  });

  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Close sidebar when navigating on mobile
  useEffect(() => { setSidebarOpen(false); }, [location.pathname]);

  useEffect(() => {
    const root = document.documentElement;
    if (isDark) {
      root.classList.remove('light');
      localStorage.setItem('ukfms-theme', 'dark');
    } else {
      root.classList.add('light');
      localStorage.setItem('ukfms-theme', 'light');
    }
  }, [isDark]);

  const handleLogout = () => {
    queryClient.clear(); // wipe cached data before navigating — prevents stale data flash on role switch
    logout();
    navigate('/login');
  };

  return (
    <div className="relative flex h-screen overflow-hidden bg-[#060a14]">
      {/* ── Mobile sidebar backdrop ── */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/60 sm:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* ── Mobile hamburger toggle ── */}
      <button
        className="sm:hidden fixed top-3 left-3 z-50 p-2 rounded-xl bg-[#070b16]/90 border border-white/10 text-slate-400 hover:text-white transition-colors"
        onClick={() => setSidebarOpen((v) => !v)}
        aria-label="Toggle navigation"
      >
        {sidebarOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
      </button>

      {/* ── Sidebar ── */}
      <aside
        className={[
          'absolute sm:relative z-40 flex flex-col shrink-0 w-64 h-full transition-transform duration-300',
          sidebarOpen ? 'translate-x-0' : '-translate-x-full sm:translate-x-0',
        ].join(' ')}
        style={{ background: 'rgba(7,11,22,0.95)', borderRight: '1px solid rgba(0,212,255,0.08)' }}
      >
        {/* Top gradient line */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/50 to-transparent" />

        {/* Brand */}
        <div className="p-6 pb-5 flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-500/25 to-purple-600/25 border border-cyan-500/30 flex items-center justify-center flex-shrink-0">
            <Shield className="w-4.5 h-4.5 text-cyan-400 w-[18px] h-[18px]" />
          </div>
          <div>
            <div className="text-sm font-bold gradient-text">UKFMS</div>
            <div className="text-[10px] text-slate-600 leading-none mt-0.5">Financial Suite</div>
          </div>
        </div>

        {/* Nav */}
        <nav className="flex-1 px-3 overflow-y-auto pb-4">
          {groups.map((group) => {
            const items = navItems.filter((n) => n.group === group.key);
            if (items.length === 0) return null;
            return (
              <div key={group.key} className="mb-6">
                <div className="text-[10px] font-semibold text-slate-700 tracking-[0.12em] px-3 mb-2">
                  {isPartner && group.partnerLabel ? group.partnerLabel : group.label}
                </div>
                {items.map(({ to, label, icon: Icon }) => {
                  const active = location.pathname === to || location.pathname.startsWith(to + '/');
                  return (
                    <NavLink
                      key={to}
                      to={to}
                      className={() =>
                        [
                          'relative flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium transition-all duration-200 mb-0.5',
                          active
                            ? 'nav-active text-cyan-300'
                            : 'text-slate-500 hover:text-slate-300 hover:bg-white/[0.03]',
                        ].join(' ')
                      }
                    >
                      <Icon className={`w-4 h-4 flex-shrink-0 ${active ? 'text-cyan-400' : ''}`} />
                      <span>{label}</span>
                      {active && (
                        <motion.div
                          layoutId="nav-indicator"
                          className="ml-auto"
                          transition={{ type: 'spring', stiffness: 400, damping: 35 }}
                        >
                          <ChevronRight className="w-3.5 h-3.5 text-cyan-500/60" />
                        </motion.div>
                      )}
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </nav>

        {/* Bottom user area */}
        <div className="p-3 border-t" style={{ borderColor: 'rgba(0,212,255,0.08)' }}>
          {/* User info */}
          <div className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white/[0.02] mb-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-cyan-500/30 to-purple-500/30 border border-cyan-500/20 flex items-center justify-center flex-shrink-0">
              <span className="text-xs font-semibold text-cyan-300">
                {email ? email[0].toUpperCase() : '?'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-xs text-slate-300 truncate">{email}</div>
              <div className={`text-[10px] font-mono mt-0.5 ${roleColor(role)}`}>
                {role?.replace(/_/g, ' ')}
              </div>
            </div>
          </div>

          {/* Action row */}
          <div className="flex gap-1">
            <button
              onClick={() => setIsDark(!isDark)}
              title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
              className="flex-1 flex items-center justify-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 py-1.5 rounded-lg hover:bg-white/[0.04] transition-colors"
            >
              {isDark ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
            </button>
            <button className="flex-1 flex items-center justify-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 py-1.5 rounded-lg hover:bg-white/[0.04] transition-colors">
              <Bell className="w-3.5 h-3.5" />
            </button>
            <button className="flex-1 flex items-center justify-center gap-1.5 text-xs text-slate-500 hover:text-slate-300 py-1.5 rounded-lg hover:bg-white/[0.04] transition-colors">
              <Settings className="w-3.5 h-3.5" />
            </button>
            <button
              onClick={handleLogout}
              className="flex-1 flex items-center justify-center gap-1.5 text-xs text-red-500/70 hover:text-red-400 py-1.5 rounded-lg hover:bg-red-500/8 transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      </aside>

      {/* ── Main content ── */}
      <main className="flex-1 overflow-auto bg-grid relative">
        <motion.div
          key={location.pathname}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25, ease: 'easeOut' }}
          className="h-full"
        >
          <Outlet />
        </motion.div>
      </main>
    </div>
  );
}

